'use strict';

Object.defineProperty(exports, '__esModule', { value: true });

function _interopDefault (ex) { return (ex && (typeof ex === 'object') && 'default' in ex) ? ex['default'] : ex; }

var _defineProperty = _interopDefault(require('@babel/runtime/helpers/defineProperty'));
var _objectWithoutProperties = _interopDefault(require('@babel/runtime/helpers/objectWithoutProperties'));
var React = _interopDefault(require('react'));
var reactHooks = require('@apollo/react-hooks');
var nthHookLoma = require('@narvar/nth-hook-loma');
var graphql = require('./graphql.js');
var proxyRetailerTheme = require('./proxyRetailerTheme.js');

var _excluded = ["children"],
  _excluded2 = ["children"],
  _excluded3 = ["children"];
function ownKeys(object, enumerableOnly) { var keys = Object.keys(object); if (Object.getOwnPropertySymbols) { var symbols = Object.getOwnPropertySymbols(object); enumerableOnly && (symbols = symbols.filter(function (sym) { return Object.getOwnPropertyDescriptor(object, sym).enumerable; })), keys.push.apply(keys, symbols); } return keys; }
function _objectSpread(target) { for (var i = 1; i < arguments.length; i++) { var source = null != arguments[i] ? arguments[i] : {}; i % 2 ? ownKeys(Object(source), !0).forEach(function (key) { _defineProperty(target, key, source[key]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(target, Object.getOwnPropertyDescriptors(source)) : ownKeys(Object(source)).forEach(function (key) { Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key)); }); } return target; }
var LomaThemeConfigContext = /*#__PURE__*/React.createContext({
  product: 'narvarjs',
  retailerMoniker: 'DEFAULT_RETAILER',
  versionName: 'narvar'
});
var ThemeContext = /*#__PURE__*/React.createContext(undefined);
var nullRetailerTheme = {}; // maintains object ref equality
var LomaThemeProvider = function LomaThemeProvider(_ref) {
  var _data$retailerTheme;
  var children = _ref.children,
    featureName = _ref.featureName,
    themeTokens = _ref.themeTokens;
  var client = nthHookLoma.useLomaClient();
  var _React$useContext = React.useContext(LomaThemeConfigContext),
    product = _React$useContext.product,
    retailerMoniker = _React$useContext.retailerMoniker,
    versionName = _React$useContext.versionName,
    localeLanguage = _React$useContext.localeLanguage,
    localeCountry = _React$useContext.localeCountry,
    cache = _React$useContext.cache,
    preventFouc = _React$useContext.preventFouc;
  var _useQuery = reactHooks.useQuery(graphql.default(themeTokens.join('\n')), {
      client: client,
      variables: {
        product: product,
        retailerMoniker: retailerMoniker,
        versionName: versionName,
        localeLanguage: localeLanguage,
        localeCountry: localeCountry,
        featureName: featureName,
        cache: cache
      }
    }),
    data = _useQuery.data,
    loading = _useQuery.loading;
  var theme = (_data$retailerTheme = data === null || data === void 0 ? void 0 : data.retailerTheme) !== null && _data$retailerTheme !== void 0 ? _data$retailerTheme : nullRetailerTheme;
  var value = React.useMemo(function () {
    return {
      loading: loading,
      theme: proxyRetailerTheme.default(theme, themeTokens)
    };
  }, [loading, theme, themeTokens]);
  return /*#__PURE__*/React.createElement(ThemeContext.Provider, {
    value: value
  }, loading && preventFouc ? /*#__PURE__*/React.createElement("span", {
    style: {
      visibility: 'hidden'
    }
  }, children) : children);
};
var ThemeProviderContext = /*#__PURE__*/React.createContext(LomaThemeProvider);
var LomaThemeConfigProvider = function LomaThemeConfigProvider(_ref2) {
  var children = _ref2.children,
    props = _objectWithoutProperties(_ref2, _excluded);
  return /*#__PURE__*/React.createElement(ThemeProviderContext.Provider, {
    value: LomaThemeProvider
  }, /*#__PURE__*/React.createElement(LomaThemeConfigContext.Provider, {
    value: props
  }, children));
};
var ThemeProvider = function ThemeProvider(props) {
  var themeTokens = props.themeTokens;
  if (!themeTokens.length) throw new Error('themeTokens must be non-empty');
  var ThemeProvider = React.useContext(ThemeProviderContext);
  return /*#__PURE__*/React.createElement(ThemeProvider, props);
};
var withThemeProvider = function withThemeProvider(component, options) {
  var _component$displayNam;
  var themeTokens = options.themeTokens;
  var HoC = /*#__PURE__*/React.forwardRef(function (props, ref) {
    var featureName = props.featureName;
    return /*#__PURE__*/React.createElement(ThemeProvider, {
      featureName: featureName,
      themeTokens: themeTokens
    }, /*#__PURE__*/React.createElement(component, _objectSpread(_objectSpread({}, props), {}, {
      ref: ref
    })));
  });
  var name = (_component$displayNam = component.displayName) !== null && _component$displayNam !== void 0 ? _component$displayNam : component.name;
  HoC.displayName = "WithThemeProvider(".concat(name, ")");
  return HoC;
};

/**
 * Optimal usage of `useTheme` requires destructuring properties immediately:
 *
 * ```
 * const { bodytextDefaultNone, bodytextEmphasisNone } = useTheme();
 * ```
 */
var useTheme = function useTheme() {
  var context = React.useContext(ThemeContext);
  if (!context) throw new Error('useTheme was called without a ThemeContext');
  return context.theme;
};

/**
 * Optimal usage of `useThemeX` requires destructuring properties immediately:
 *
 * ```
 * const {
 *   loading,
 *   theme: {
 *     bodytextDefaultNone,
 *     bodytextEmphasisNone
 *   }
 * } = useThemeX();
 * ```
 */
var useThemeX = function useThemeX() {
  var context = React.useContext(ThemeContext);
  if (!context) {
    throw new Error('useThemeX was called without a ThemeContext');
  }
  return context;
};
var LocalThemeConfigContext = /*#__PURE__*/React.createContext({
  queryTheme: function queryTheme() {
    throw new Error('Not implemented');
  }
});
var LocalThemeProvider = function LocalThemeProvider(_ref3) {
  var children = _ref3.children,
    props = _objectWithoutProperties(_ref3, _excluded2);
  var config = React.useContext(LocalThemeConfigContext);
  var value = config.queryTheme(props);
  return /*#__PURE__*/React.createElement(ThemeContext.Provider, {
    value: value
  }, children);
};
var LocalThemeConfigProvider = function LocalThemeConfigProvider(_ref4) {
  var children = _ref4.children,
    props = _objectWithoutProperties(_ref4, _excluded3);
  return /*#__PURE__*/React.createElement(ThemeProviderContext.Provider, {
    value: LocalThemeProvider
  }, /*#__PURE__*/React.createElement(LocalThemeConfigContext.Provider, {
    value: props
  }, children));
};

exports.retailerThemeQuery = graphql.default;
exports.LocalThemeConfigProvider = LocalThemeConfigProvider;
exports.LocalThemeProvider = LocalThemeProvider;
exports.LomaThemeConfigProvider = LomaThemeConfigProvider;
exports.ThemeProvider = ThemeProvider;
exports.useTheme = useTheme;
exports.useThemeX = useThemeX;
exports.withThemeProvider = withThemeProvider;
